<script setup lang="ts">
import { useLayout } from '@/composables/layout';
import AppFooter from '@/layout/AppFooter.vue';
import { UserType } from '@/lib/types/user';
import { SUPPORTED_LOCALES } from '@/plugins/i18n';
import { getUserTypeLabel } from '@/plugins/i18n/enums';
import { useAuthStore } from '@/store/auth';
import { storeToRefs } from 'pinia';
import type { SelectChangeEvent } from 'primevue/select';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const auth = useAuthStore();
const { user } = storeToRefs(auth);
const { currentLanguage, setLanguage } = useLayout();
const { t } = useI18n();

// Opzioni di lingua basate sulle locali supportate
const languageOptions = SUPPORTED_LOCALES.map(loc => ({
    label: t(`languages.${loc}`),
    value: loc
}));

// Computed per il v-model
const selectedLanguage = computed({
    get: () => currentLanguage.value,
    set: (value) => setLanguage(value)
});

// Gestisci cambio lingua
const updateLocale = (e: SelectChangeEvent) => {
    setLanguage(e.value);
};

// Logout
const logout = () => {
    auth.logout();
};

const userTypes = computed(() => [
    { label: t(getUserTypeLabel(UserType.User)), value: UserType.User, icon: 'fa-solid fa-user' },
    { label: t(getUserTypeLabel(UserType.Admin)), value: UserType.Admin, icon: 'fa-solid fa-user-tie' },
    { label: t(getUserTypeLabel(UserType.SuperAdmin)), value: UserType.SuperAdmin, icon: 'fa-solid fa-user-shield' }
]);
</script>

<template>
    <div class="flex flex-col h-full gap-4">
        <!-- User Header -->
        <div v-if="user" class="flex flex-col items-center gap-3 pb-4">
            <Avatar icon="fa-regular fa-user" size="xlarge" shape="circle" class="bg-primary-100 text-primary-700" />
            <div class="text-center">
                <h3 class="text-lg font-semibold m-0">
                    {{ user.firstName }} {{ user.lastName }}
                </h3>
                <Chip :label="t(getUserTypeLabel(user.type))" class="mt-2" />
            </div>
        </div>
        <Divider class="my-0" />

        <!-- User Info -->
        <div v-if="user" class="flex flex-col gap-2">
            <div class="flex items-center gap-3 p-3 bg-surface-50 rounded-lg">
                <i class="fa-regular fa-envelope text-surface-500"></i>
                <span>{{ user.email }}</span>
            </div>
            <div class="flex items-center gap-3 p-3 bg-surface-50 rounded-lg">
                <i class="fa-regular fa-user text-surface-500"></i>
                <span>{{ user.username }}</span>
            </div>
        </div>

        <Divider class="my-0" />

        <!-- Language Selector -->
        <div class="flex flex-col gap-2">
            <label class="font-medium flex items-center gap-2">
                <i class="fa-solid fa-globe"></i>
                {{ t('settings.language') }}
            </label>
            <Select v-model="selectedLanguage" :options="languageOptions" option-label="label" option-value="value"
                @change="updateLocale" class="w-full" />
        </div>

        <!-- Spacer -->
        <div class="flex-grow"></div>

        <!-- Logout Button -->
        <Button icon="fa-solid fa-arrow-right-from-bracket" :label="t('settings.logout')" severity="danger" outlined
            class="w-full justify-center" @click="logout" />

        <app-footer></app-footer>
    </div>
</template>
